import { useState, useMemo } from 'react';
import { DefaultRecordType } from 'rc-table/lib/interface';
import { set } from 'lodash';

import history from 'browserHistory';
import { GridState } from '../interfaces';
import { filterBy, orderBy } from '../utils';
import { GridSort, GridFilter, GridPagination } from '../utils/interfaces';
import { useSessionStorage } from '../../hooks/useLocalStorage';

export const useGridState = (data: DefaultRecordType[], state: GridState, beforePageChanged?: any) => {
  const keyLS = history.location.pathname;

  const [stateLS, setStateLS] = useSessionStorage(keyLS, state);
  const [sort, setSort] = useState<GridSort | undefined>(stateLS.sort);
  const [filter, _setFilter] = useState<GridFilter | undefined>(stateLS.filter);

  const [pagination, setPagination] = useState<GridPagination | undefined>(stateLS.pagination);

  const handleSortChange = (gridSort: GridSort) => {
    const newState = set(stateLS, 'sort', gridSort);

    setStateLS(newState);
    setSort(gridSort);
  };

  const handlePageChange = (event: GridPagination) => {
    if (beforePageChanged) {
      beforePageChanged();
    }

    const newPagination = {
      skip: event.skip,
      pageSize: event.pageSize,
    };
    const newState = set(stateLS, 'pagination', newPagination);
    setStateLS(newState);
    setPagination(newPagination);
  };

  const setFirstPage = () => {
    if (pagination?.skip !== 0) {
      const newPagination = {
        skip: 0,
        pageSize: 15,
      };
      const newState = set(stateLS, 'pagination', newPagination);

      setStateLS(newState);
      setPagination(newPagination);
    }
  };

  const setFilter = (event: GridFilter) => {
    _setFilter(event);
  };

  const processedData = useMemo(() => {
    let tempData = data;
    if (sort) {
      tempData = orderBy(tempData, sort);
    }
    if (filter) {
      tempData = filterBy(tempData, filter);
    }

    return tempData;
  }, [sort, filter, data]);

  const total = useMemo(() => processedData.length, [processedData]);

  const gridData = useMemo(() => {
    if (!pagination) {
      return processedData;
    }
    return processedData.slice(pagination.skip, pagination.pageSize + pagination.skip);
  }, [processedData, pagination]);

  return {
    sort,
    filter,
    pagination,
    gridData,
    total,
    handleSortChange,
    handlePageChange,
    setFirstPage,
    setFilter,
  };
};
